import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import api from '../api';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormHelperText from '@mui/material/FormHelperText';
import MenuItem from '@mui/material/MenuItem';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import LeftPanel from '../components/LeftPanel';
import PrimaryButton from '../components/PrimaryButton';
import ConfirmSubmissionModal from '../components/ConfirmSubmissionModal';
import InfoModal from '../components/InfoModal';
import FileUpload from '../components/FileUpload';
import useUnsavedChangesWarning from '../hooks/useUnsavedChangesWarning';
import UnsavedChangesModal from '../components/UnsavedChangesModal';

function countWords(s) {
    if (!s) return 0;
    return s.trim().split(/\s+/).filter(Boolean).length;
}

const formatPhone = (val) => {
    if (!val) return '';
    const digits = String(val).replace(/\D/g, '').slice(0, 10);
    const len = digits.length;
    if (len === 0) return '';
    if (len < 4) return digits;
    if (len < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

function limitToWords(s, max) {
    if (!s) return '';
    const tokens = s.split(/(\s+)/);
    let wordCount = 0;
    const out = [];
    for (let i = 0; i < tokens.length; i++) {
        const t = tokens[i];
        if (/^\s+$/.test(t)) {
            if (out.length > 0) out.push(t);
            else out.push(t);
        } else {
            if (t.trim() === '') { out.push(t); continue; }
            if (wordCount < max) { out.push(t); wordCount++; }
            else break;
        }
    }
    return out.join('');
}

export default function ILGForm() {
    const [questions, setQuestions] = useState([]); // raw order corresponds to DB columns
    const questionCount = questions.length;
    const [answers, setAnswers] = useState(() => new Array(0).fill(''));

    // derived ordered list used only for rendering/numbering. Each item contains rawIndex.
    const displayQuestions = React.useMemo(() => {
        return questions
            .map((q, i) => ({ ...q, rawIndex: i }))
            .sort((a, b) => {
                const oa = a.order !== undefined ? a.order : a.rawIndex + 1;
                const ob = b.order !== undefined ? b.order : b.rawIndex + 1;
                return oa - ob;
            });
    }, [questions]);
    const [fileAnswers, setFileAnswers] = useState(() => Array.from({ length: 0 }, () => []));
    const [schoolName, setSchoolName] = useState('');
    const [lastUpdated, setLastUpdated] = useState(null);
    const [saved, setSaved] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const navigate = useNavigate();

    const isLong = (i) => questions[i] && questions[i].type === 'long';
    const isDropdown = (i) => questions[i] && questions[i].type === 'dropdown';

    const loadData = async (countOverride, qMetaArray) => {
        setLoading(true);
        const cnt = typeof countOverride === 'number' ? countOverride : questionCount;
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/public/ilg-applications', { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            if (res.data && res.data.exists) {
                if (res.data.completed) {
                    navigate('/grantlanding');
                    return;
                }
                setSaved(true);
                const next = new Array(cnt).fill('');
                const filesNext = Array.from({ length: cnt }, () => []);
                for (let i = 0; i < cnt; i++) {
                    // answers are still stored in raw order (questions array)
                    const qMeta = (qMetaArray && qMetaArray[i]) || questions[i];
                    // Read only the new/descriptive DB fields. Do not fall back to legacy `ilg_question_*` fields.
                    const raw = qMeta && qMeta.dbField ? res.data[qMeta.dbField] : null;
                    const rawFiles = qMeta && qMeta.filesDbField ? res.data[qMeta.filesDbField] : (qMeta && qMeta.dbField ? res.data[`${qMeta.dbField}_files`] : null);
                    // Flexible parsing: some questions store files in the main column, others in a separate _files column.
                    if (qMeta && qMeta.type === 'file') {
                        // primary storage may be in the descriptive db field or its `_files` companion
                        if (raw) {
                            try {
                                next[i] = JSON.parse(raw);
                            } catch (e) {
                                next[i] = Array.isArray(raw) ? raw : [];
                            }
                        } else if (rawFiles) {
                            try {
                                next[i] = JSON.parse(rawFiles);
                            } catch (e) {
                                next[i] = Array.isArray(rawFiles) ? rawFiles : [];
                            }
                        } else {
                            next[i] = [];
                        }
                        // Debug: log raw payload for file questions to aid troubleshooting when uploads don't reappear
                        try {
                            // eslint-disable-next-line no-console
                            console.debug('ILGForm: loaded file question', { index: i + 1, raw, rawFiles, parsed: next[i] });
                        } catch (e) { /* ignore logging errors */ }
                    } else {
                        next[i] = raw || '';
                    }
                    // load separate files column if present (e.g., ilg_question_24_files)
                    if (qMeta && qMeta.hasFiles) {
                        if (rawFiles) {
                            try {
                                filesNext[i] = JSON.parse(rawFiles);
                            } catch (e) {
                                filesNext[i] = Array.isArray(rawFiles) ? rawFiles : [];
                            }
                        } else if (raw) {
                            // sometimes the files were saved into the primary db field as JSON
                            try {
                                const maybe = JSON.parse(raw);
                                filesNext[i] = Array.isArray(maybe) ? maybe : [];
                            } catch (e) {
                                filesNext[i] = [];
                            }
                        } else {
                            filesNext[i] = [];
                        }
                        try {
                            // eslint-disable-next-line no-console
                            console.debug('ILGForm: loaded files column', { index: i + 1, rawFiles, parsedFiles: filesNext[i] });
                        } catch (e) { /* ignore logging errors */ }
                    }
                }
                setAnswers(next);
                setFileAnswers(filesNext);
                if (res.data.schoolName) setSchoolName(res.data.schoolName);
                if (res.data.last_updated_by_first_name || res.data.updated_at || res.data.last_saved_at) {
                    setLastUpdated({
                        firstName: res.data.last_updated_by_first_name,
                        lastName: res.data.last_updated_by_last_name,
                        email: res.data.last_updated_by_email,
                        updatedAt: res.data.updated_at || res.data.last_saved_at
                    });
                }
                // record snapshot for dirty-check
                if (initialSnapshot.current) initialSnapshot.current = { answers: JSON.stringify(next), fileAnswers: JSON.stringify(filesNext), schoolName: res.data.schoolName || '' };
            } else {
                setSaved(false);
                if (initialSnapshot.current) initialSnapshot.current = { answers: JSON.stringify(new Array(cnt).fill('')), fileAnswers: JSON.stringify(Array.from({ length: cnt }, () => [])), schoolName: '' };
            }
        } catch (err) {
            console.error(err);
        } finally { setLoading(false); }
    };

    // fetch server-hosted question metadata (label + type) and then load saved data
    useEffect(() => {
        let mounted = true;
        axios.get('/api/public/questions/ilg').then(res => {
            if (!mounted) return;
            const arr = Array.isArray(res.data) ? res.data : [];
            const merged = arr.map((meta, idx) => ({
                label: meta.label || '',
                type: meta.type || 'text',
                required: meta.required !== undefined ? meta.required : true,
                hasFiles: meta.hasFiles !== undefined ? meta.hasFiles : false,
                dbField: meta.dbField || meta.db_field || meta.name || `ilg_question_${idx + 1}`,
                filesDbField: meta.filesDbField || meta.files_db_field || meta.files_db || null,
                legacyFilesDbField: meta.legacyFilesDbField || meta.legacy_files_db_field || null,
                order: (meta.order !== undefined && meta.order !== null) ? meta.order : (idx + 1),
            }));
            setQuestions(merged);
            setAnswers(new Array(merged.length).fill(''));
            setFileAnswers(Array.from({ length: merged.length }, () => []));
            loadData(merged.length, merged);
        }).catch(() => {
            loadData(0);
        });
        return () => { mounted = false; };
    }, []);

    // fetch authenticated user info so we can show school name immediately
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const me = await api.get('/api/public/me');
                const name = me?.data?.schoolName || me?.data?.school_name || '';
                if (mounted && name) setSchoolName(name);
            } catch (e) {
                // ignore
            }
        })();
        return () => { mounted = false; };
    }, []);

    const initialSnapshot = useRef({ answers: JSON.stringify([]), fileAnswers: JSON.stringify([]), schoolName: '' });

    const isDirty = () => {
        try {
            if (!initialSnapshot.current) return false;
            if (saving) return false;
            if (JSON.stringify(answers) !== initialSnapshot.current.answers) return true;
            if (JSON.stringify(fileAnswers) !== initialSnapshot.current.fileAnswers) return true;
            if ((schoolName || '') !== (initialSnapshot.current.schoolName || '')) return true;
            return false;
        } catch (e) { return false; }
    };

    const pendingResolve = useRef(null);
    const [unsavedModalOpen, setUnsavedModalOpen] = useState(false);
    const showPrompt = () => new Promise((resolve) => {
        pendingResolve.current = resolve;
        setUnsavedModalOpen(true);
    });
    const handleModalClose = () => {
        setUnsavedModalOpen(false);
        if (pendingResolve.current) { pendingResolve.current(false); pendingResolve.current = null; }
    };
    const handleModalConfirm = () => {
        setUnsavedModalOpen(false);
        if (pendingResolve.current) { pendingResolve.current(true); pendingResolve.current = null; }
    };

    useUnsavedChangesWarning(isDirty, 'You have unsaved changes on this ILG form. Save draft or cancel to continue.', showPrompt);

    const formatToEastern = (iso) => {
        if (!iso) return '';
        try {
            const d = new Date(iso);
            const datePart = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' });
            const timePart = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: 'America/New_York' });
            return `${datePart} - ${timePart} Eastern`;
        } catch (e) { return iso; }
    };

    const determineFolder = (qObj) => {
        const label = (qObj && qObj.label) ? String(qObj.label).toLowerCase() : '';
        const db = (qObj && qObj.dbField) ? String(qObj.dbField).toLowerCase() : '';
        const filesDb = (qObj && qObj.filesDbField) ? String(qObj.filesDbField).toLowerCase() : '';
        // explicit override: if metadata indicates this is question 23 (order=23), treat as CAD
        if (qObj && (qObj.order === 23 || qObj.order === '23')) return 'cad';
        if (db.includes('cad') || filesDb.includes('cad') || label.includes('cad')) return 'cad';
        if (label.includes('intended') || db.includes('intended') || filesDb.includes('intended')) return 'intended_space';
        return 'potential_space';
    };

    const firstMissingIndex = () => {
        for (let i = 0; i < answers.length; i++) {
            // If question 1 (index 0) is "Yes", question 2 (index 1) is not required.
            if (i === 1 && answers[0] === 'Yes') continue;
            // skip non-required questions (explicit required: false in metadata)
            const qMeta = questions[i];
            const required = qMeta ? (qMeta.required !== false) : true;
            if (!required) continue;
            // for questions that have files as well as text, ensure both present
            if (qMeta && qMeta.hasFiles) {
                if (answers[i] === null || answers[i] === undefined) return i;
                if (String(answers[i]).trim().length === 0) return i;
                if (!fileAnswers[i] || fileAnswers[i].length === 0) return i;
                continue;
            }
            if (qMeta && qMeta.type === 'file') {
                if (!answers[i] || answers[i].length === 0) return i;
                continue;
            }
            if (answers[i] === null || answers[i] === undefined) return i;
            if (String(answers[i]).trim().length === 0) return i;
        }
        return -1;
    };

    const handleChange = (index, value) => {
        const copy = [...answers];
        const qType = questions[index] ? questions[index].type : null;
        if (qType === 'phone') {
            copy[index] = formatPhone(value);
        } else if (isLong(index)) copy[index] = limitToWords(value, 200);
        else copy[index] = value;
        setAnswers(copy);
    };

    const saveDraft = async () => {
        setSaving(true);
        try {
            const token = localStorage.getItem('token');
            const payload = {};
            for (let i = 0; i < answers.length; i++) {
                const qMeta = questions[i];
                const legacyKey = `ilg_question_${i + 1}`;
                const dbKey = qMeta && qMeta.dbField ? qMeta.dbField : legacyKey;
                if (qMeta && qMeta.type === 'file') {
                    payload[dbKey] = answers[i] && answers[i].length ? JSON.stringify(answers[i]) : null;
                } else {
                    payload[dbKey] = answers[i];
                }
                if (qMeta && qMeta.hasFiles) {
                    const dbFilesKey = qMeta && qMeta.filesDbField ? qMeta.filesDbField : `${dbKey}_files`;
                    payload[dbFilesKey] = fileAnswers[i] && fileAnswers[i].length ? JSON.stringify(fileAnswers[i]) : null;
                }
            }
            payload.completed = false;
            await axios.post('/api/public/ilg-applications', payload, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            setSaved(true);
            await loadData();
            // update snapshot to saved state
            initialSnapshot.current = { answers: JSON.stringify(answers), fileAnswers: JSON.stringify(fileAnswers), schoolName: schoolName || '' };
            setSnackbarMessage('Draft saved');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            setAttemptedSubmit(false);
            // notify other components (ProgressSteps) to refresh status
            try { window.dispatchEvent(new Event('formsUpdated')); } catch (e) { /* ignore */ }
        } catch (err) {
            console.error(err);
            setSnackbarMessage('Failed to save draft');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally { setSaving(false); }
    };

    const submitApplication = async () => {
        const miss = firstMissingIndex();
        if (miss !== -1) {
            setSnackbarMessage('Please complete required fields');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            try { scrollToAndHighlight(miss); } catch (e) { }
            return;
        }
        setSaving(true);
        try {
            const token = localStorage.getItem('token');
            const payload = {};
            for (let i = 0; i < answers.length; i++) {
                const qMeta = questions[i];
                const legacyKey = `ilg_question_${i + 1}`;
                const dbKey = qMeta && qMeta.dbField ? qMeta.dbField : legacyKey;
                if (qMeta && qMeta.type === 'file') {
                    payload[dbKey] = answers[i] && answers[i].length ? JSON.stringify(answers[i]) : null;
                } else {
                    payload[dbKey] = answers[i];
                }
                if (qMeta && qMeta.hasFiles) {
                    const dbFilesKey = qMeta && qMeta.filesDbField ? qMeta.filesDbField : `${dbKey}_files`;
                    payload[dbFilesKey] = fileAnswers[i] && fileAnswers[i].length ? JSON.stringify(fileAnswers[i]) : null;
                }
            }
            payload.completed = true;
            await axios.post('/api/public/ilg-applications', payload, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            setSaved(true);
            await loadData();
            // update snapshot to saved state
            initialSnapshot.current = { answers: JSON.stringify(answers), fileAnswers: JSON.stringify(fileAnswers), schoolName: schoolName || '' };
            setSnackbarMessage('Application submitted');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            // notify other components (ProgressSteps) to refresh status before redirect
            try { window.dispatchEvent(new Event('formsUpdated')); } catch (e) { /* ignore */ }
            navigate('/grantlanding');
        } catch (err) {
            console.error(err);
            setSnackbarMessage('Failed to submit application');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally { setSaving(false); }
    };

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [infoOpen, setInfoOpen] = useState(false);
    const [infoQuestion, setInfoQuestion] = useState('');
    const questionRefs = useRef({});
    const [attemptedSubmit, setAttemptedSubmit] = useState(false);

    const attemptSubmit = () => {
        setAttemptedSubmit(true);
        const miss = firstMissingIndex();
        if (miss !== -1) {
            setSnackbarMessage('Please complete required fields');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            try { scrollToAndHighlight(miss); } catch (e) { }
            return;
        }
        setConfirmOpen(true);
    };

    const scrollToAndHighlight = (index) => {
        // try to find a named form element first
        const named = document.querySelector(`[name="q_${index}"]`);
        const container = questionRefs.current && questionRefs.current[index];
        const el = named || container;
        if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            try { el.focus && el.focus({ preventScroll: true }); } catch (e) { try { el.focus && el.focus(); } catch (e2) { /* ignore */ } }
        }
        // do not apply temporary orange highlight anymore; rely on persistent error styling
    };

    const handleCloseSnackbar = () => setSnackbarOpen(false);

    const SECTIONS = [
        { title: 'General Information', indices: [0, 1, 2], columns: 1 },
        { title: 'Primary Contact Information', indices: [3, 4, 5, 6, 7, 8], columns: 2 },
        { title: 'Secondary Contact Information', indices: [9, 10, 11, 12, 13, 14], columns: 2 },
        { title: 'Facilities Manager Contact Information', indices: [15, 16, 17, 18, 19, 20], columns: 2 },
        { title: 'Information About Your Space', indices: [21, 22, 23, 24, 25, 26, 27, 28], columns: 1 },
        { title: 'Information About Your Project', indices: [29, 30, 31, 32, 33], columns: 1 }
    ];

    // Helper: for facilities section, prefer ordering by common contact-field keywords
    const orderFacilitiesIndices = (indices) => {
        if (!questions || questions.length === 0) return indices;
        const patterns = ['first', 'last', 'title', 'department', 'email', 'phone'];
        const picked = new Set();
        const out = [];
        const lowerLabel = (i) => (questions[i] && questions[i].label ? String(questions[i].label).toLowerCase() : '');
        for (const p of patterns) {
            for (const idx of indices) {
                if (picked.has(idx)) continue;
                const lbl = lowerLabel(idx);
                if (lbl.includes(p)) {
                    out.push(idx);
                    picked.add(idx);
                    break;
                }
            }
        }
        // append any remaining indices in original order
        for (const idx of indices) {
            if (!picked.has(idx)) out.push(idx);
        }
        return out;
    };

    const renderQuestionField = (qObj, idx, val) => {
        if (!qObj) return null;
        const disabled = (idx === 1 && answers[0] === 'Yes');
        const words = isLong(idx) ? countWords(val) : 0;
        const atLimit = isLong(idx) && words >= 200;
        const isReq = qObj.required !== false;
        const labelSx = { fontWeight: 600, mb: 1, color: disabled ? 'text.disabled' : 'inherit' };

        const isMissing = isReq && !disabled && (val === null || val === undefined || String(val).trim().length === 0);

        if (qObj.type === 'date') {
            return (
                <>
                    <Typography sx={labelSx}>{idx + 1}. {qObj.label}{isReq && <span style={{ color: '#ff6600' }}> *</span>}</Typography>
                    <TextField
                        name={`q_${idx}`}
                        type="date"
                        value={val ? String(val).split('T')[0] : ''}
                        onChange={(e) => handleChange(idx, e.target.value ? new Date(e.target.value).toISOString() : '')}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        disabled={disabled}
                        required={isReq}
                        error={attemptedSubmit && isMissing}
                        helperText={attemptedSubmit && isMissing ? 'Required' : ''}
                    />
                </>
            );
        }

        if (isDropdown(idx)) {
            return (
                <>
                    <Typography sx={labelSx}>{idx + 1}. {qObj.label}{isReq && <span style={{ color: '#ff6600' }}> *</span>}</Typography>
                    <TextField
                        select
                        name={`q_${idx}`}
                        value={val}
                        onChange={(e) => handleChange(idx, e.target.value)}
                        fullWidth
                        disabled={disabled}
                        required={isReq}
                        error={attemptedSubmit && isMissing}
                        helperText={attemptedSubmit && isMissing ? 'Required' : ''}
                    >
                        <MenuItem value="">-- Select --</MenuItem>
                        <MenuItem value="Yes">Yes</MenuItem>
                        <MenuItem value="No">No</MenuItem>
                    </TextField>
                </>
            );
        }

        if (qObj.type === 'phone') {
            return (
                <>
                    <Typography sx={labelSx}>{idx + 1}. {qObj.label}{isReq && <span style={{ color: '#ff6600' }}> *</span>}</Typography>
                    <TextField
                        name={`q_${idx}`}
                        value={val}
                        onChange={(e) => handleChange(idx, e.target.value)}
                        fullWidth
                        inputProps={{ inputMode: 'tel', maxLength: 14 }}
                        placeholder="(xxx) xxx-xxxx"
                        disabled={disabled}
                        required={isReq}
                        error={attemptedSubmit && isMissing}
                        helperText={attemptedSubmit && isMissing ? 'Required' : ''}
                    />
                </>
            );
        }

        return (
            <>
                <Typography sx={labelSx}>{idx + 1}. {qObj.label}{isReq && <span style={{ color: '#ff6600' }}> *</span>}</Typography>
                <TextField
                    name={`q_${idx}`}
                    value={val}
                    onChange={(e) => handleChange(idx, e.target.value)}
                    fullWidth
                    multiline={isLong(idx)}
                    minRows={isLong(idx) ? 3 : 1}
                    maxRows={isLong(idx) ? 20 : undefined}
                    variant="outlined"
                    disabled={disabled}
                    required={isReq}
                    error={attemptedSubmit && isMissing}
                    helperText={attemptedSubmit && isMissing ? 'Required' : (isLong(idx) ? `${words} / 200 words${atLimit ? ' — word limit reached' : ''}` : '')}
                />
            </>
        );
    };

    return (
        <>
            <Box component="main">
                <Grid container spacing={4} alignItems="flex-start" sx={{ mt: 0 }}>
                    <Grid item xs={12} md={3} lg={3}>
                        <LeftPanel items={["THE DEADLINE IS 11:59PM EST, JUNE 7, 2026.", "Insititutions have the chance to be awarded an Innovation Lounge Grant for their campus. An Innovation Lounge is a space on campus where students can come together to ideate, collaborate on projects and be innovative in their schoolwork. An Innovation Lounge can also be used to explore career pathways and conduct hybrid meetings and webinars.", "You can find the FAQs here."]}>
                            <PrimaryButton
                                onClick={() => {
                                    setInfoQuestion('What is the Innovation Lounge Grant?');
                                    setInfoOpen(true);
                                }}
                                sx={{ bgcolor: '#fff', color: '#000', textTransform: 'none' }}
                            >
                                FAQ
                            </PrimaryButton>
                            <PrimaryButton
                                onClick={() => {
                                    setInfoQuestion('What is the breakdown of the Innovation Lounge Grant?');
                                    setInfoOpen(true);
                                }}
                                sx={{ bgcolor: '#fff', color: '#000', textTransform: 'none', ml: 1 }}
                            >
                                Breakdown
                            </PrimaryButton>
                            <InfoModal
                                open={infoOpen}
                                onClose={() => {
                                    setInfoOpen(false);
                                    setInfoQuestion('');
                                }}
                                openQuestion={infoQuestion}
                            />
                        </LeftPanel>
                    </Grid>
                    <Grid item xs={12} md={9} lg={9}>
                        <Paper elevation={1} sx={{ p: { xs: 0, md: 0 }, position: 'relative', mx: 'auto', maxWidth: { xs: '100%', md: '950px' } }}>
                            <Box sx={{ position: 'sticky', top: 0, background: '#fff', zIndex: 2, p: { xs: 3, md: 4 }, borderBottom: '1px solid #eee' }}>
                                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Innovation Lounge Grant</Typography>
                                <Typography variant="subtitle1" sx={{ mb: 0 }}>School: {schoolName}</Typography>
                                {lastUpdated && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        Last updated by {lastUpdated.firstName || ''} {lastUpdated.lastName || ''} ({lastUpdated.email || ''}) on {formatToEastern(lastUpdated.updatedAt)}
                                    </Typography>
                                )}
                                {/* note about required fields */}
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1, fontStyle: 'italic' }}>
                                    <span style={{ color: '#ff6600' }}>*</span> indicates a required field. Please complete all required items.
                                </Typography>
                            </Box>

                            <Box component="form" noValidate sx={{ maxHeight: '72vh', overflow: 'auto', p: { xs: 3, md: 5 } }}>
                                <Grid container spacing={2}>
                                    {SECTIONS.map((sec, sidx) => (
                                        <Grid item xs={12} key={sidx}>
                                            <Box sx={{ border: '1px solid #eee', p: 2, mb: 2 }}>
                                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{sec.title}</Typography>
                                                <Grid container spacing={2}>
                                                    {(sec.title === 'Facilities Manager Contact Information' ? orderFacilitiesIndices(sec.indices) : sec.indices).map((idx) => {
                                                        const qObj = questions[idx];
                                                        if (!qObj) return null;
                                                        const isFile = qObj && qObj.type === 'file';
                                                        const hasFiles = qObj && qObj.hasFiles;
                                                        const val = isFile ? (answers[idx] || []) : (answers[idx] || '');
                                                        const filesVal = hasFiles ? (fileAnswers[idx] || []) : (isFile ? (answers[idx] || []) : []);
                                                        const isContactGrid = sec.columns && sec.columns > 1;
                                                        const md = isContactGrid ? Math.floor(12 / sec.columns) : 12;
                                                        const isReq = qObj.required !== false;
                                                        return (
                                                            <Grid
                                                                item
                                                                xs={12}
                                                                md={md}
                                                                key={idx}
                                                                ref={(el) => { questionRefs.current[idx] = el; }}
                                                                tabIndex={-1}
                                                            >
                                                                {isFile ? (
                                                                    (() => {
                                                                        const token = localStorage.getItem('token');
                                                                        const folder = determineFolder(qObj);
                                                                        return (
                                                                            <>
                                                                                <Typography sx={{ fontWeight: 600, mb: 1 }}>{idx + 1}. {qObj.label}{isReq && <span style={{ color: '#ff6600' }}> *</span>}</Typography>
                                                                                <FileUpload
                                                                                    folder={folder}
                                                                                    existing={val}
                                                                                    token={token}
                                                                                    onChange={(list) => {
                                                                                        const copy = [...answers];
                                                                                        copy[idx] = list || [];
                                                                                        setAnswers(copy);
                                                                                    }}
                                                                                />
                                                                                {attemptedSubmit && isReq && (!val || (Array.isArray(val) && val.length === 0)) && (
                                                                                    <FormHelperText sx={{ color: 'error.main', mt: 0.5 }}>Required</FormHelperText>
                                                                                )}
                                                                            </>
                                                                        );
                                                                    })()
                                                                ) : hasFiles ? (
                                                                    (() => {
                                                                        const token = localStorage.getItem('token');
                                                                        // render text field + file upload
                                                                        return (
                                                                            <>
                                                                                {renderQuestionField(qObj, idx, val)}
                                                                                <FileUpload
                                                                                    folder={determineFolder(qObj)}
                                                                                    existing={filesVal}
                                                                                    token={token}
                                                                                    onChange={(list) => {
                                                                                        const copy = [...fileAnswers];
                                                                                        copy[idx] = list || [];
                                                                                        setFileAnswers(copy);
                                                                                    }}
                                                                                />
                                                                                {attemptedSubmit && isReq && (!filesVal || (Array.isArray(filesVal) && filesVal.length === 0)) && (
                                                                                    <FormHelperText sx={{ color: 'error.main', mt: 0.5 }}>Required</FormHelperText>
                                                                                )}
                                                                            </>
                                                                        );
                                                                    })()
                                                                ) : (
                                                                    renderQuestionField(qObj, idx, val)
                                                                )}
                                                            </Grid>
                                                        );
                                                    })}
                                                </Grid>
                                            </Box>
                                        </Grid>
                                    ))}

                                    <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 1 }}>
                                        <PrimaryButton onClick={saveDraft} disabled={saving || loading}>{'Save Draft'}</PrimaryButton>
                                        <PrimaryButton onClick={attemptSubmit} disabled={saving || loading}>{'Submit'}</PrimaryButton>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
            <ConfirmSubmissionModal open={confirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={async () => { setConfirmOpen(false); await submitApplication(); }} />
            <UnsavedChangesModal
                open={unsavedModalOpen}
                onClose={handleModalClose}
                onConfirm={handleModalConfirm}
                title="Unsaved Changes"
                message={"It looks like you have unsaved changes on your Innovation Lounge form. Click Cancel to return to your form, then scroll to the bottom and save your work or hit \"OK\" to lose your changes."}
            />

            <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
}

